import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { knowledgeBaseFiles } from '../../../../models/axiosModel'
import { Grid, TableCell } from '@mui/material'; 
import globalStyles from "../../../../assets/styles/global.module.scss";
import dashboardStyles from '../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'; 
import customAPIStyle from "../../../../assets/styles/componentStyles/customApiStyles/customApiStyle.module.scss";
import { formatDateAccordingToTimezoneUsingMoment, formatFileSize, getValueOrDefault } from '../../../../utils/constants'; 
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GlobalTooltip from '../../../utility/tooltip/globalTooltip';
import { getVendorField } from '../../../../utils/whiteLabelling';
import { UserContext, UserContextPayload } from '../../../../context/userContext';

const TRUNCATION_EPSILON = 1;

type KnowledgeBaseFileNameLinkProps = {
    fileName: string;
    googleStorageId: string | undefined;
};

const KnowledgeBaseFileNameLink: React.FC<KnowledgeBaseFileNameLinkProps> = ({ fileName, googleStorageId }) => {
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const { vendorData } = useContext(UserContext) as UserContextPayload;

    const updateTruncation = useCallback(() => {
        const el = anchorRef.current;
        if (!el || !fileName) {
            setIsTruncated(false);
            return;
        }
        const truncatedVertically = el.scrollHeight > el.clientHeight + TRUNCATION_EPSILON;
        const truncatedHorizontally = el.scrollWidth > el.clientWidth + TRUNCATION_EPSILON;
        setIsTruncated(truncatedVertically || truncatedHorizontally);
    }, [fileName]);

    useLayoutEffect(() => {
        updateTruncation();
    }, [fileName, updateTruncation]);

    useEffect(() => {
        const el = anchorRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const ro = new ResizeObserver(() => {
            updateTruncation();
        });
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, [updateTruncation]);

    const href = `${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${googleStorageId}&flag_info=false`;

    const anchor = (
        <a
            ref={anchorRef}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={style.truncateProductImageUrl}
        >
            {fileName}
        </a>
    );

    if (!fileName || !isTruncated) {
        return anchor;
    }

    return (
        <GlobalTooltip title={fileName} placement="top">
            {anchor}
        </GlobalTooltip>
    );
};

interface MemoizedKnowledgeBaseTableRowProps {
    data: knowledgeBaseFiles,
    userTimezone: string, 
    handleMenuOpen: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, data: knowledgeBaseFiles) => void;
} 

const MemoizedKnowledgeBaseTableRow: React.FC<MemoizedKnowledgeBaseTableRowProps> = React.memo((props) => { 

    const { data, userTimezone, handleMenuOpen } = props; 
    return (
        <>
            <TableCell 
                className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}
            >
                <KnowledgeBaseFileNameLink
                    fileName={data?.file_name ?? ''}
                    googleStorageId={String(data?.google_storage?.google_storage_id)}
                />
            </TableCell>  
            <TableCell 
                className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}
            >
                {formatFileSize(data?.google_storage?.google_storage_file_size, "")}
            </TableCell> 
            <TableCell 
                className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}
            >
                {data?.assignments?.length > 0 ? 
                    <Grid container gap={"8px"}>
                        {data?.assignments?.map((assignment) => {
                                return (
                                    <Grid item className={`${customAPIStyle.chatbotNameChip} ${globalStyles.clickable}`} onClick={(e) => {e.stopPropagation(); window.open(`${window.location.origin}/chatbot/viewChatbot?chatbot_id=${assignment?.chatbot?.chatbot_id}`, "_blank")}}>
                                        {getValueOrDefault(assignment?.chatbot?.chatbot_name)}
                                    </Grid>
                                );
                            })
                        }
                    </Grid>
                    : 
                    "-"
                }
            </TableCell>
            <TableCell 
                className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}
            >
                {formatDateAccordingToTimezoneUsingMoment(data?.added_timestamp, userTimezone)}
            </TableCell>  
            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon} ${style.checkBoxCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter} ${globalStyles.contentEnd}`}>
                <MoreVertIcon
                    className={globalStyles.clickable}
                    onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
                        handleMenuOpen(e, data);
                    }}
                />
            </TableCell>
        </>
    )
}, (prev, next) => {
    return prev.data == next.data;
})

export default MemoizedKnowledgeBaseTableRow
